import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject, tap } from 'rxjs';


import { environment } from "src/environments/environment";
import { AppStateService } from "../app-state.service";
import { OwpStateService } from "./owp-state.service";

type HttpMethod = "get" | "post";
type UrlLike = string | unknown[] | Record<string, unknown>;
type HttpOptionsPayload = object;
type LegacyResponse = LegacyAny;

interface DeviceInfoPayload {
  device12nc?: string;
  softwareVersion?: string;
}

const FEATURE_PAYLOAD_PATH_SEGMENT = "feature-payload";
const OWP_ACCESS_PASSWORD_QUERY_PARAM_NAME = "owpAccessPassword"; // NOSONAR: query-string key name, not a password value
const USER_PREFERENCES_DEBOUNCE_MS = 500;

/**
 * MultiOneHttpClient
 * --------------------------------------------------------------------
 * All HTTP concerns previously baked into `ApiService`:
 *
 * - REST endpoint constants (preferences, configuration, schemas, ...)
 * - Low-level helpers: `apiCall(method, url, body?, options?)` and
 *   `putApi(...)` (kept as-is for backward compatibility)
 * - Domain helpers: `getStartConfiguration`, `getConfigurationAPI`,
 *   `getSupportedSchemaList`, `getDeviceSoftwareVersion`, etc.
 * - `postCallForUserPreferences()`  persists the current UI-preferences
 *   snapshot back to the server after a 500ms debounce.
 * - A small `Map`-backed configuration-data cache.
 *
 * `ApiService` delegates every one of these methods here so consumers
 * continue to call `apiService.getStartConfiguration(...)` unchanged.
 */
@Injectable({ providedIn: "root" })
export class MultiOneHttpClient {
  // --- REST endpoint constants -------------------------------------
  readonly preferencesAPI = "config/api/v1/preferences";
  readonly startConfigURL = "config/api/v1/configuration";
  readonly readerStartUrl = "/server/v1/status";
  readonly getConfigurationUrl =
    "config/api/v1/configuration/{configurationId}/features";
  readonly scehmaLIstApi = "config/api/v1/devices/schemas?12nc=";
  readonly fullDeviceList = "search/api/v1/search/v1/devices?showArchive=true";
  readonly softwareAPI = "search/api/v1/search/v1/device/{device12Nc}/software";
  readonly productionInformationAPI =
    "search/api/v1/data/devices/{deviceNfcId}/production-information";
  readonly setFeatureUrl = "config/api/v1/configuration/{configurationId}/features";
  readonly midNightShiftCalculationUrl = "config/api/v1/midnight-shift-calculation";

  /** Cancel-token for the debounced preferences POST. */
  readonly unsubscribe = new Subject<void>();

  /** Free-form scratch pad preserved from legacy `ApiService`. */
  apiList: unknown[] = [];

  private readonly configDataCache = new Map<string, unknown>();

  constructor(
    private readonly http: HttpClient,
    private readonly appState: AppStateService,
    private readonly owp: OwpStateService,
  ) {}

  // --- Cache -------------------------------------------------------
  getCachedConfigData(configId: string): unknown {
    return this.configDataCache.get(configId);
  }

  setCachedConfigData(configId: string, data: unknown): void {
    this.configDataCache.set(configId, data);
  }

  hasCachedConfigData(configId: string): boolean {
    return this.configDataCache.has(configId);
  }

  clearConfigCache(): void {
    this.configDataCache.clear();
  }

  // Legacy passthroughs retained for specs/components still treating
  // HTTP + OWP signals as one service surface.
  sendOwpPasswordSelected(isSelected: boolean): void {
    this.owp.sendOwpPasswordSelected(isSelected);
  }

  sendOwpAccessPwd(values: unknown): void {
    if (typeof values === "string" || Array.isArray(values) || values === null) {
      this.owp.sendOwpAccessPwd(values as string | unknown[] | null);
      return;
    }
    this.owp.sendOwpAccessPwd("");
  }

  // Legacy no-op retained for tests that still spy on this method
  // from the old monolithic ApiService surface.
  sendMultiFeatures(_data: unknown): void {
    /* handled by AvailableFeaturesStateService in refactored flow */
  }

  // --- Low-level HTTP helpers --------------------------------------
  /**
   * Legacy method retained verbatim  dispatches to `HttpClient.get` /
   * `HttpClient.post` based on the `method` argument, and appends the
   * OWP access password to URLs that include `feature-payload`
   * when password authentication has been selected by the user.
   */
  apiCall<T = LegacyResponse>(
    method: string,
    url: UrlLike,
    body?: unknown,
    httpOptions?: HttpOptionsPayload,
  ): Observable<T> | undefined {
    const normalizedUrl = Array.isArray(url) ? url.join("") : String(url);
    // Snapshot the current OWP password + selected flag (single-shot).
    let owpPasswordSelected = false;
    this.owp.getowpPasswordSelected.subscribe((data) => {
      owpPasswordSelected = data;
    });
    let owpAccessPwd = "";
    this.owp.getowpAccessPwd.subscribe((data) => {
      owpAccessPwd = Array.isArray(data)
        ? data.map((value) => String(value).trim()).join(",")
        : (data ?? "");
    });

    if (method === "get") {
      if (
        normalizedUrl.includes(FEATURE_PAYLOAD_PATH_SEGMENT) &&
        owpPasswordSelected &&
        owpAccessPwd !== ""
      ) {
        const securedUrl = `${normalizedUrl}?${OWP_ACCESS_PASSWORD_QUERY_PARAM_NAME}=${owpAccessPwd}`;
        return this.http.get<T>(securedUrl, httpOptions);
      }
      return this.http.get<T>(normalizedUrl, httpOptions);
    }
    if (method === "post") {
      return this.http.post<T>(normalizedUrl, body, httpOptions);
    }
    return undefined;
  }

  putApi<T = LegacyResponse>(
    method: string,
    url: UrlLike,
    body?: unknown,
    httpOptions?: HttpOptionsPayload,
  ): Observable<T> | undefined {
    const normalizedUrl = Array.isArray(url) ? url.join("") : String(url);
    if (method === "put") {
      return this.http.put<T>(normalizedUrl, body, httpOptions);
    }
    return undefined;
  }

  // --- Domain helpers ----------------------------------------------
  generatefile(featureFileUrl: string, configurationID: string): Observable<string> {
    const featureUrl = featureFileUrl.replace("{configurationId}", configurationID);
    return this.http.get(featureUrl, { responseType: "text" }).pipe(tap());
  }

  getStartConfiguration(
    deviceInfo: DeviceInfoPayload,
    url: UrlLike,
  ): Observable<LegacyResponse> | undefined {
    const normalizedUrl = Array.isArray(url) ? url.join("") : String(url);
    return this.apiCall<LegacyResponse>("post", normalizedUrl + this.startConfigURL, {
      device12nc: deviceInfo.device12nc,
      softwareVersion: deviceInfo.softwareVersion,
    });
  }

  getMidnightShiftCalculation(payload: unknown, url: UrlLike): Observable<LegacyResponse> | undefined {
    const normalizedUrl = Array.isArray(url) ? url.join("") : String(url);
    return this.apiCall<LegacyResponse>("post", normalizedUrl + this.midNightShiftCalculationUrl, payload);
  }

  getServerStatus(url: UrlLike): Observable<LegacyResponse> | undefined {
    const normalizedUrl = Array.isArray(url) ? url.join("") : String(url);
    return this.apiCall<LegacyResponse>("get", normalizedUrl + this.readerStartUrl);
  }

  getConfigurationAPI(url: UrlLike, configID: string | number): Observable<LegacyResponse> | undefined {
    const normalizedUrl = Array.isArray(url) ? url.join("") : String(url);
    const target = (normalizedUrl + this.getConfigurationUrl).replace("{configurationId}", String(configID));
    return this.apiCall<LegacyResponse>("get", target);
  }

  getSupportedSchemaList(
    url: string,
    device12nc: string,
    DeviceVersion: string,
  ): Observable<LegacyResponse> | undefined {
    return this.apiCall<LegacyResponse>(
      "get",
      url + this.scehmaLIstApi + device12nc + "&DeviceVersion=" + DeviceVersion,
    );
  }

  getDeviceSoftwareVersion(
    url: string,
    device12nc: string | Record<string, unknown>,
  ): Observable<LegacyResponse> | undefined {
    const target = (url + this.softwareAPI).replace("{device12Nc}", String(device12nc));
    return this.apiCall<LegacyResponse>("get", target);
  }

  getProductionInfo(url: string, deviceNfcId: string): Observable<LegacyResponse> | undefined {
    const target = (url + this.productionInformationAPI).replace(
      "{deviceNfcId}",
      deviceNfcId,
    );
    return this.apiCall<LegacyResponse>("get", target);
  }

  setAllfeatureAPI(
    url: string,
    configId: string | number,
    features: unknown,
  ): Observable<LegacyResponse> | undefined {
    const target = (url + this.setFeatureUrl).replace("{configurationId}", String(configId));
    return this.apiCall<LegacyResponse>("post", target, features);
  }

  /**
   * Debounced 500ms POST that serialises the current UI-preferences
   * (columns, filters, sort, DCF / feature-file toggles, ...) back to
   * the server. Preserves the pre-refactor behaviour verbatim.
   */
  postCallForUserPreferences(): void {
    setTimeout(() => {
      const storeSorting = this.appState.getLastSorting() ?? [];
      const storeFilter = this.appState.getStoreFilter() ?? [];
      const storeColumn = this.appState.getStoreColumn() ?? [];
      const storeOmitOption = this.appState.getStoreOmitOption() ?? [];
      const storeConfigMode = this.appState.getStoreConfigMode() ?? [];
      const storeSaveFeatureFile = this.appState.getStoreSaveFeatureFile() ?? [];
      const storeDCFfile = this.appState.getStoreDCFFile() ?? [];

      const sortingObjectKey = {
        storeColumn: {
          storeColumnsData: storeColumn["storeColumnsData"],
          storeColumnsCheck: storeColumn["storeColumnsCheck"],
        },
        storeOmitOption: {
          storeDevicePopupValue: storeOmitOption["storeDevicePopupValue"],
          storeDevicePopupCheck: storeOmitOption["storeDevicePopupCheck"],
        },
        storeConfigMode: {
          storeConfigModeData: storeConfigMode["storeConfigModeData"],
          storeConfigModeCheck: storeConfigMode["storeConfigModeCheck"],
        },
        lastSorting: {
          storeLastSortedColumn: storeSorting["storeLastSortedColumn"],
          storeLastColumnCheck: storeSorting["storeLastColumnCheck"],
        },
        sortCol: {},
        storeFilter: {
          storeFilterData: storeFilter["storeFilterData"],
          storeFilterCheck: storeFilter["storeFilterCheck"],
        },
        storeSaveFeatureFile: {
          storeSaveFeatureFileCheck: storeSaveFeatureFile["storeSaveFeatureFileCheck"],
          storeSaveFeatureFileCheckValue:
            storeSaveFeatureFile["storeSaveFeatureFileCheckValue"],
        },
        storeDCFFile: {
          storeDCFFileCheck: storeDCFfile["storeDCFFileCheck"],
          storeDCFFileCheckValue: storeDCFfile["storeDCFFileCheckValue"],
        },
        storeConcecutiveMultiple: this.appState.getStoreConcecutiveMultiple(),
      };

      this.unsubscribe.next(undefined);
      this.apiCall(
        "post",
        environment.multiOneAPIUrl + this.preferencesAPI,
        sortingObjectKey,
      )?.subscribe(() => {
        /* response intentionally ignored */
      });
    }, USER_PREFERENCES_DEBOUNCE_MS);
  }
}
